import { useEffect, useState } from 'react'
import { supabaseClient } from '../config/supabase'
import { useAdminScope } from '../hooks/useAdminScope'
import {
  GraduationCap,
  Plus,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronRight,
  Save,
  X,
  AlertCircle,
  Search,
} from 'lucide-react'
import type {
  Department,
  Program,
  ProgramEducationalOutcome,
  DepartmentWithPrograms,
  ProgramWithPEOs,
  OutcomeType,
} from '../types'

export default function ManagePrograms() {
  const { isSuperAdmin, canManageDepartment, loading: scopeLoading } = useAdminScope()
  const [departments, setDepartments] = useState<DepartmentWithPrograms[]>([])
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set())
  const [expandedPrograms, setExpandedPrograms] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openAddMenu, setOpenAddMenu] = useState<string | null>(null) // Track which program's add menu is open

  // Modal states
  const [showDepartmentModal, setShowDepartmentModal] = useState(false)
  const [showProgramModal, setShowProgramModal] = useState(false)
  const [showPEOModal, setShowPEOModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [messageModal, setMessageModal] = useState<{ title: string; message: string; type: 'error' | 'success' | 'warning' }>({ 
    title: '', 
    message: '', 
    type: 'error' 
  })
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null)
  const [editingProgram, setEditingProgram] = useState<Program | null>(null)
  const [editingPEO, setEditingPEO] = useState<ProgramEducationalOutcome | null>(null)
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null)
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'department' | 'program' | 'peo'; id: string; name: string } | null>(null)
  const [keywordInput, setKeywordInput] = useState('') // For custom keyword input
  const [showKeywordSuggestions, setShowKeywordSuggestions] = useState(false) // Show/hide suggestions
  const [isHoveringSuggestions, setIsHoveringSuggestions] = useState(false) // Track hover over suggestions

  // Form states
  const [departmentForm, setDepartmentForm] = useState({
    name: '',
    abbreviation: '',
    description: '',
    display_order: 0,
    createAdminCode: false,
  })
  const [_generatedAdminCode, setGeneratedAdminCode] = useState<string | null>(null)
  const [programForm, setProgramForm] = useState({
    name: '',
    code: '',
    full_name: '',
    description: '',
    display_order: 0,
  })
  const [peoForm, setPEOForm] = useState({
    peo_number: 1,
    title: '',
    description: '',
    keywords: [] as string[],
    outcome_type: 'PEO' as OutcomeType,
  })
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [activeOutcomeTab, setActiveOutcomeTab] = useState<OutcomeType>('PEO') // Toggle between PEO and PO views
  const [searchQuery, setSearchQuery] = useState('')

  const filteredDepartments = searchQuery.trim()
    ? departments
        .map((dept) => {
          const q = searchQuery.toLowerCase()
          const deptMatch =
            dept.name.toLowerCase().includes(q) ||
            dept.abbreviation?.toLowerCase().includes(q)
          const matchingPrograms = (dept.programs || []).filter(
            (p) =>
              p.name.toLowerCase().includes(q) ||
              p.code?.toLowerCase().includes(q) ||
              p.full_name?.toLowerCase().includes(q)
          )
          if (deptMatch) return dept
          if (matchingPrograms.length > 0)
            return { ...dept, programs: matchingPrograms }
          return null
        })
        .filter(Boolean) as typeof departments
    : departments

  // Categorized keywords for job matching
  const KEYWORD_CATEGORIES = {
    'Programming & Software': [
      'API Development', 'Backend Development', 'Code Review', 'Debugging', 'Frontend Development',
      'Full Stack Development', 'Integration Testing', 'Legacy Code', 'Microservices',
      'Mobile Development', 'Programming', 'Quality Assurance', 'Refactoring',
      'Software Architecture', 'Software Development', 'Software Engineering', 'Software Maintenance',
      'Test Automation', 'Testing', 'Unit Testing', 'Web Development',
    ],
    'Programming Languages': [
      'Bash', 'C', 'C#', 'C++', 'CSS', 'Go', 'HTML', 'Java', 'JavaScript', 'Kotlin',
      'MATLAB', 'Perl', 'PHP', 'PowerShell', 'Python', 'R', 'Ruby', 'Rust', 'SQL',
      'Scala', 'Swift', 'TypeScript', 'VB.NET',
    ],
    'Frameworks & Libraries': [
      '.NET Core', 'ASP.NET', 'Angular', 'Bootstrap', 'Django', 'Express.js', 'Flask',
      'Laravel', 'Material-UI', 'Node.js', 'React', 'Ruby on Rails', 'Spring Boot',
      'Tailwind CSS', 'Vue.js', 'jQuery',
    ],
    'Databases': [
      'Cassandra', 'Database Design', 'Database Management', 'Database Optimization',
      'DynamoDB', 'Firebase', 'MongoDB', 'MySQL', 'Microsoft SQL Server', 'Oracle Database',
      'PostgreSQL', 'Query Optimization', 'Redis', 'SQL', 'SQLite',
    ],
    'Data & Analytics': [
      'A/B Testing', 'Big Data', 'Business Intelligence', 'Data Analysis', 'Data Cleaning',
      'Data Engineering', 'Data Mining', 'Data Modeling', 'Data Science', 'Data Visualization',
      'Data Warehousing', 'ETL', 'Excel', 'Google Analytics', 'Power BI', 'Predictive Analytics',
      'Statistical Analysis', 'Tableau',
    ],
    'AI & Machine Learning': [
      'Artificial Intelligence', 'Computer Vision', 'Deep Learning', 'Feature Engineering',
      'Keras', 'Machine Learning', 'Model Training', 'Natural Language Processing',
      'Neural Networks', 'PyTorch', 'Reinforcement Learning', 'Scikit-learn', 'TensorFlow',
    ],
    'Cloud & DevOps': [
      'AWS', 'Ansible', 'Azure', 'CI/CD', 'Cloud Architecture', 'Cloud Computing',
      'Cloud Security', 'Configuration Management', 'DevOps', 'Docker', 'GitHub Actions',
      'GitLab CI', 'Google Cloud Platform', 'Infrastructure as Code', 'Jenkins',
      'Kubernetes', 'Microservices Architecture', 'Serverless', 'Terraform',
    ],
    'Cybersecurity': [
      'Application Security', 'Cybersecurity', 'Encryption', 'Ethical Hacking',
      'Firewall Management', 'Incident Response', 'Information Security', 'Intrusion Detection',
      'Network Security', 'Penetration Testing', 'Risk Assessment', 'SIEM',
      'Security Auditing', 'Security Compliance', 'Security Operations', 'Vulnerability Assessment',
    ],
    'Networking & IT': [
      'Active Directory', 'Backup and Recovery', 'Cisco', 'DHCP', 'DNS', 'Hardware Support',
      'Help Desk', 'Hyper-V', 'IT Support', 'LAN/WAN', 'Linux Administration',
      'Network Administration', 'Network Configuration', 'Network Monitoring',
      'Network Security', 'Network Troubleshooting', 'Routing', 'Server Management',
      'Software Installation', 'Switching', 'System Administration', 'System Monitoring',
      'TCP/IP', 'Technical Support', 'Troubleshooting', 'VMware', 'VPN',
      'Virtualization', 'WiFi Management', 'Windows Server',
    ],
    'Electrical & Electronics': [
      'Analog Electronics', 'Arduino', 'Circuit Analysis', 'Circuit Design', 'Control Systems',
      'Digital Electronics', 'Electronics', 'Embedded Systems', 'FPGA', 'Instrumentation',
      'Microcontrollers', 'PCB Design', 'PLC Programming', 'Power Electronics',
      'Raspberry Pi', 'Signal Processing', 'VLSI Design',
    ],
    'Mechanical Engineering': [
      '3D Modeling', '3D Printing', 'AutoCAD', 'CAD Design', 'CAM', 'CATIA', 'CNC Programming',
      'Creo', 'FEA', 'Fabrication', 'Fluid Mechanics', 'Heat Transfer', 'Machining',
      'Mechanical Design', 'Product Design', 'Prototyping', 'SolidWorks', 'Stress Analysis',
      'Thermodynamics', 'Welding',
    ],
    'Industrial & Manufacturing': [
      'Cost Reduction', 'Factory Automation', 'ISO Standards', 'Industrial Engineering',
      'Inventory Management', 'Kaizen', 'Lean Manufacturing', 'Manufacturing',
      'Operations Management', 'Process Improvement', 'Process Optimization',
      'Production Planning', 'Production Scheduling', 'Quality Control', 'Quality Management',
      'Six Sigma', 'Statistical Process Control', 'Supply Chain Optimization',
      'Warehouse Management',
    ],
    'Civil & Construction': [
      'Blueprint Reading', 'Building Codes', 'Civil Engineering', 'Construction Management',
      'Cost Estimation', 'Environmental Engineering', 'Estimation', 'Geotechnical Engineering',
      'Project Planning', 'Site Supervision', 'Structural Engineering', 'Surveying',
      'Transportation Engineering', 'Water Resources',
    ],
    'Project Management': [
      'Agile Methodology', 'Budget Management', 'Gantt Charts', 'Jira', 'Kanban',
      'MS Project', 'PMP', 'PRINCE2', 'Project Coordination', 'Project Management',
      'Project Planning', 'Project Scheduling', 'Resource Management', 'Risk Management',
      'Scrum', 'Stakeholder Management', 'Trello', 'Waterfall',
    ],
    'Business & Management': [
      'Business Analysis', 'Business Development', 'Change Management', 'Contract Management',
      'KPI Tracking', 'Leadership', 'Operations Management', 'Organizational Development',
      'People Management', 'Performance Management', 'Strategic Management', 'Strategic Planning',
      'Team Building', 'Team Management', 'Vendor Management',
    ],
    'Finance & Accounting': [
      'Accounting', 'Accounts Payable', 'Accounts Receivable', 'Auditing', 'Bookkeeping',
      'Budgeting', 'Cash Flow Management', 'Cost Accounting', 'External Audit',
      'Financial Accounting', 'Financial Analysis', 'Financial Planning', 'Financial Reporting',
      'Forecasting', 'GAAP', 'IFRS', 'Internal Audit', 'Management Accounting',
      'Oracle Financials', 'Payroll', 'QuickBooks', 'SAP', 'Tax Compliance', 'Tax Preparation',
    ],
    'Marketing & Sales': [
      'Account Management', 'B2B Sales', 'B2C Sales', 'Brand Management', 'CRM',
      'Client Relations', 'Content Marketing', 'Customer Insights', 'Digital Marketing',
      'Email Marketing', 'Facebook Ads', 'Google Ads', 'Lead Generation', 'Market Research',
      'Marketing', 'Marketing Analytics', 'Marketing Strategy', 'Negotiation', 'SEO', 'SEM',
      'Sales', 'Sales Forecasting', 'Sales Management', 'Salesforce', 'Social Media Marketing',
    ],
    'Human Resources': [
      'Compensation and Benefits', 'Employee Engagement', 'Employee Relations', 'HR Compliance',
      'HR Management', 'HRIS', 'Human Resources', 'Interviewing', 'Labor Relations',
      'Onboarding', 'Performance Evaluation', 'Recruitment', 'Succession Planning',
      'Talent Acquisition', 'Training and Development', 'Workforce Planning',
    ],
    'Customer Service': [
      'Call Center', 'Client Communication', 'Client Service', 'Complaint Resolution',
      'Customer Experience', 'Customer Retention', 'Customer Service', 'Customer Support',
      'Help Desk Support', 'Service Excellence', 'Technical Support',
    ],
    'Education & Training': [
      'Child Development', 'Classroom Management', 'Curriculum Development', 'E-Learning',
      'ESL', 'Educational Assessment', 'Educational Research', 'Educational Technology',
      'Instruction', 'Instructional Design', 'Learning Management Systems', 'Lesson Planning',
      'Mentoring', 'Pedagogy', 'Special Education', 'Student Counseling', 'Student Evaluation',
      'TESOL', 'Teaching', 'Training Delivery',
    ],
    'Healthcare & Nursing': [
      'CPR', 'Clinical Assessment', 'Emergency Care', 'Emergency Response', 'First Aid',
      'HIPAA Compliance', 'Health Education', 'Health Information Management',
      'Healthcare Management', 'Hospital Administration', 'IV Therapy', 'Infection Control',
      'Medical Procedures', 'Medical Records', 'Medical Terminology', 'Medication Administration',
      'Nursing Care', 'Patient Assessment', 'Patient Care', 'Patient Education', 'Pharmacology',
      'Triage', 'Vital Signs Monitoring', 'Wound Care',
    ],
    'Medical Specialties': [
      'Anesthesiology', 'Cardiology', 'Gynecology', 'Laboratory', 'Mental Health', 'Neurology',
      'Obstetrics', 'Occupational Therapy', 'Pathology', 'Pediatrics', 'Physical Therapy',
      'Psychiatry', 'Psychology', 'Radiology', 'Rehabilitation', 'Surgery',
    ],
    'Maritime & Logistics': [
      'Cargo Operations', 'Customs Clearance', 'Deck Operations', 'ECDIS', 'Engine Room Operations',
      'Freight Forwarding', 'Logistics', 'Marine Electronics', 'Marine Engineering',
      'Maritime Navigation', 'Maritime Regulations', 'Maritime Safety', 'Navigation',
      'Port Operations', 'STCW', 'Ship Maintenance', 'Ship Navigation', 'Ship Operations',
      'Shipping Documentation', 'Supply Chain Management', 'Transportation Management',
      'Vessel Operations',
    ],
    'Law & Public Safety': [
      'Case Analysis', 'Community Policing', 'Crime Prevention', 'Crime Scene Investigation',
      'Criminal Investigation', 'Disaster Response', 'Emergency Management', 'Fire Safety',
      'Forensic Science', 'Forensics', 'Law Enforcement', 'Legal Research', 'Legal Writing',
      'Occupational Safety', 'Physical Security', 'Public Safety', 'Security Management',
      'Security Operations', 'Surveillance',
    ],
    'Trade & Customs': [
      'Border Security', 'Customs Procedures', 'Customs Regulations', 'Customs Valuation',
      'HS Code', 'Import/Export', 'Incoterms', 'International Trade', 'Tariff Classification',
      'Trade Compliance', 'Trade Documentation', 'Trade Finance',
    ],
    'Design & Creative': [
      'Adobe Illustrator', 'Adobe InDesign', 'Adobe Photoshop', 'Adobe Premiere', 'Animation',
      'Brand Design', 'Canva', 'Color Theory', 'Content Creation', 'Figma', 'Graphic Design',
      'Logo Design', 'Motion Graphics', 'Photography', 'Sketch', 'Typography', 'UI Design',
      'UX Design', 'Video Editing', 'Web Design',
    ],
    'Architecture & Planning': [
      'Architectural Design', 'Architecture', 'BIM', 'Building Design', 'Interior Design',
      'Landscape Architecture', 'Rendering', 'Revit', 'SketchUp', 'Space Planning',
      'Sustainable Design', 'Urban Planning', '3D Visualization',
    ],
    'Science & Research': [
      'Biology', 'Biotechnology', 'Chemistry', 'Data Collection', 'Environmental Science',
      'Experimentation', 'Geology', 'Hypothesis Testing', 'Laboratory Skills',
      'Laboratory Testing', 'Literature Review', 'Microbiology', 'Physics', 'Research',
      'Research Methodology', 'Scientific Research', 'Scientific Writing',
    ],
    'Agriculture & Food': [
      'Agricultural Economics', 'Agriculture', 'Agronomy', 'Animal Husbandry', 'Crop Production',
      'Food Processing', 'Food Safety', 'Food Science', 'Food Technology', 'Horticulture',
      'Nutrition', 'Pest Management', 'Quality Assurance', 'Soil Science',
    ],
    'Hospitality & Tourism': [
      'Concierge Services', 'Customer Hospitality', 'Event Management', 'Event Planning',
      'Food and Beverage Service', 'Front Office Operations', 'Guest Relations',
      'Hospitality Management', 'Hotel Management', 'Housekeeping Management',
      'Restaurant Management', 'Tourism Management', 'Travel Planning',
    ],
    'Media & Communications': [
      'Broadcasting', 'Content Strategy', 'Copywriting', 'Corporate Communications',
      'Crisis Communication', 'Editing', 'Internal Communications', 'Journalism',
      'Media Relations', 'News Writing', 'Press Release Writing', 'Proofreading',
      'Public Relations', 'Reporting', 'Social Media Management',
    ],
    'Soft Skills': [
      'Accountability', 'Accuracy', 'Active Listening', 'Adaptability', 'Analytical Skills',
      'Attention to Detail', 'Coaching', 'Collaboration', 'Communication', 'Conflict Resolution',
      'Continuous Improvement', 'Creativity', 'Critical Thinking', 'Creative Thinking',
      'Cultural Awareness', 'Customer Focus', 'Decision Making', 'Delegation',
      'Diversity and Inclusion', 'Emotional Intelligence', 'Ethics', 'Flexibility',
      'Initiative', 'Innovation', 'Integrity', 'Interpersonal Skills', 'Leadership',
      'Logical Reasoning', 'Mentoring', 'Motivation', 'Multi-tasking', 'Organization',
      'Presentation Skills', 'Prioritization', 'Problem Solving', 'Professionalism',
      'Public Speaking', 'Quality Focus', 'Reliability', 'Resilience', 'Self-motivation',
      'Service Orientation', 'Strategic Thinking', 'Stress Management', 'Team Coordination',
      'Teamwork', 'Time Management', 'Verbal Communication', 'Work Ethic', 'Written Communication',
    ],
    'Languages': [
      'Arabic', 'English', 'Filipino', 'French', 'German', 'Interpretation', 'Japanese',
      'Korean', 'Mandarin', 'Multilingual', 'Spanish', 'Tagalog', 'Translation',
    ],
    'Business Tools': [
      'Data Entry', 'Database Management', 'ERP Systems', 'Google Docs', 'Google Sheets',
      'Google Workspace', 'Microsoft Dynamics', 'Microsoft Excel', 'Microsoft Office',
      'Microsoft Outlook', 'Microsoft PowerPoint', 'Microsoft Word', 'Oracle ERP',
      'SAP ERP', 'Spreadsheet Management',
    ],
    'Documentation & Quality': [
      'Audit', 'Benchmarking', 'Best Practices', 'Business Writing', 'Compliance',
      'Documentation', 'Grant Writing', 'ISO 14001', 'ISO 45001', 'ISO 9001',
      'Manual Creation', 'Policy Writing', 'Process Documentation', 'Proposal Writing',
      'Quality Management System', 'Regulatory Compliance', 'Report Writing',
      'Requirements Documentation', 'Standard Operating Procedures', 'Standards Compliance',
      'Technical Writing',
    ],
    'Version Control': [
      'Bitbucket', 'Code Collaboration', 'Code Documentation', 'Git', 'GitHub', 'GitLab',
      'Pull Requests', 'SVN', 'Version Control',
    ],
    'Emerging Technologies': [
      '5G Technology', 'AR/VR', 'Augmented Reality', 'Automation', 'Blockchain',
      'Cryptocurrency', 'Drones', 'Edge Computing', 'IoT', 'Internet of Things',
      'Quantum Computing', 'Robotics', 'Smart Devices', 'Virtual Reality',
    ],
    'Academic & Core Subjects': [
      'Algebra', 'Biology', 'Calculus', 'Chemistry', 'Economics', 'English', 'Geometry',
      'History', 'Mathematics', 'Math', 'Physics', 'Statistics', 'Trigonometry',
    ],
    'Miscellaneous': [
      'Advisory', 'Community Outreach', 'Consulting', 'Contract Negotiation', 'Coordination',
      'Facilitation', 'Grant Management', 'Partnership Development', 'Planning',
      'Policy Development', 'Procurement', 'Program Development', 'Program Management',
      'Purchasing', 'Stakeholder Engagement',
    ],
  }

  useEffect(() => {
    fetchDepartmentsWithPrograms()
  }, [])

  // Close add menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openAddMenu && !(event.target as Element).closest('.relative')) {
        setOpenAddMenu(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openAddMenu])

  // Helper function to show message modal
  const showMessage = (title: string, message: string, type: 'error' | 'success' | 'warning' = 'error') => {
    setMessageModal({ title, message, type })
    setShowMessageModal(true)
  }

  const fetchDepartmentsWithPrograms = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch departments
      const { data: depts, error: deptsError } = await supabaseClient
        .from('departments')
        .select('*')
        .order('display_order', { ascending: true })

      if (deptsError) throw deptsError

      // Fetch programs
      const { data: progs, error: progsError } = await supabaseClient
        .from('programs')
        .select('*')
        .order('display_order', { ascending: true })

      if (progsError) throw progsError

      // Fetch PEOs
      const { data: peos, error: peosError } = await supabaseClient
        .from('program_educational_outcomes')
        .select('*')
        .order('peo_number', { ascending: true })

      if (peosError) throw peosError

      // Organize data hierarchically
      const deptsWithPrograms: DepartmentWithPrograms[] = (depts || []).map((dept: Department) => {
        const deptPrograms = (progs || []).filter((p: Program) => p.department_id === dept.id)
        const programsWithPEOs: ProgramWithPEOs[] = deptPrograms.map((prog: Program) => ({
          ...prog,
          peos: (peos || []).filter((peo: ProgramEducationalOutcome) => peo.program_id === prog.id),
        }))
        return {
          ...dept,
          programs: programsWithPEOs,
        }
      })

      setDepartments(deptsWithPrograms)
    } catch (err: any) {
      console.error('Error fetching data:', err)
      setError(err.message || 'Failed to load departments and programs')
    } finally {
      setLoading(false)
    }
  }

  // Department CRUD operations
  const openAddDepartmentModal = () => {
    setEditingDepartment(null)
    setDepartmentForm({ name: '', abbreviation: '', description: '', display_order: 0, createAdminCode: false })
    setGeneratedAdminCode(null)
    setShowDepartmentModal(true)
  }

  const openEditDepartmentModal = (dept: Department) => {
    setEditingDepartment(dept)
    setDepartmentForm({
      name: dept.name,
      abbreviation: dept.abbreviation,
      description: dept.description || '',
      display_order: dept.display_order || 0,
      createAdminCode: false,
    })
    setGeneratedAdminCode(null)
    setShowDepartmentModal(true)
  }

  const saveDepartment = async () => {
    try {
      if (!departmentForm.name || !departmentForm.abbreviation) {
        showMessage('Missing Required Fields', 'Please fill in Department Name and Abbreviation.', 'warning')
        return
      }

      if (editingDepartment) {
        // Update existing department
        const { error } = await supabaseClient
          .from('departments')
          .update({
            name: departmentForm.name,
            abbreviation: departmentForm.abbreviation,
            description: departmentForm.description || null,
            display_order: departmentForm.display_order || null,
          })
          .eq('id', editingDepartment.id)

        if (error) throw error
      } else {
        // Insert new department
        const { error } = await supabaseClient.from('departments').insert({
          name: departmentForm.name,
          abbreviation: departmentForm.abbreviation,
          description: departmentForm.description || null,
          display_order: departmentForm.display_order || null,
        })

        if (error) throw error

        // Generate admin code if requested
        if (departmentForm.createAdminCode) {
          const code = `${departmentForm.abbreviation.toUpperCase()}_ADMIN_${new Date().getFullYear()}`
          const expiresAt = new Date()
          expiresAt.setHours(expiresAt.getHours() + 24)

          const { data: userData } = await supabaseClient.auth.getUser()
          if (userData?.user) {
            const { error: codeError } = await supabaseClient
              .from('admin_codes')
              .insert({
                code: code,
                created_by: userData.user.id,
                expires_at: expiresAt.toISOString(),
                is_active: true,
                admin_type: 'department',
                for_department: departmentForm.name,
              })

            if (codeError) {
              console.error('Failed to create admin code:', codeError)
              showMessage('Department Created', `Department created successfully, but failed to create admin code: ${codeError.message}`, 'warning')
            } else {
              setGeneratedAdminCode(code)
              showMessage('Success!', `Department created! Admin code: ${code} (valid for 24 hours)`, 'success')
            }
          }
        }
      }

      setShowDepartmentModal(false)
      fetchDepartmentsWithPrograms()
    } catch (err: any) {
      console.error('Error saving department:', err)
      showMessage('Save Failed', err.message || 'Failed to save department. Please try again.', 'error')
    }
  }

  const openDeleteDepartmentModal = async (dept: Department) => {
    try {
      const fullDept = departments.find(d => d.id === dept.id) as DepartmentWithPrograms | undefined
      const programCodes = fullDept?.programs?.map(p => p.code) || []
      const peoIds = fullDept?.programs?.flatMap(p =>
        (p as ProgramWithPEOs).peos?.map(peo => peo.id) || []
      ) || []

      const { data: surveys, error: surveysError } = await supabaseClient
        .from('surveys')
        .select('id, title, target_departments, target_programs, questions')
        .neq('status', 'draft')

      if (surveysError) throw surveysError

      const allSurveys = surveys || []
      const byDept = allSurveys.filter((s: any) => s.target_departments?.includes(dept.name))
      const byProgram = allSurveys.filter((s: any) =>
        programCodes.length > 0 && s.target_programs?.some((p: string) => programCodes.includes(p))
      )
      const byPEO = allSurveys.filter((s: any) =>
        peoIds.length > 0 && Array.isArray(s.questions) &&
        s.questions.some((q: any) => q.peo_ids?.some((id: string) => peoIds.includes(id)))
      )

      const seen = new Set<string>()
      const blocking = [...byDept, ...byProgram, ...byPEO].filter(s => {
        if (seen.has(s.id)) return false
        seen.add(s.id)
        return true
      })

      if (blocking.length > 0) {
        const names = blocking.map((s: any) => `"${s.title}"`).join(', ')
        const reasons: string[] = []
        if (byDept.length > 0) reasons.push('directly targeted to this department')
        if (byProgram.length > 0) reasons.push('deployed to a program in this department')
        if (byPEO.length > 0) reasons.push('linked to PEOs/POs from this department')
        const reasonText = reasons.join(' or ')
        showMessage(
          'Cannot Delete Department',
          `This department cannot be deleted because active survey(s) ${names} ${blocking.length === 1 ? 'is' : 'are'} currently ${reasonText}. Please delete or update those surveys first.`,
          'warning'
        )
        return
      }
    } catch {
      showMessage('Check Failed', 'Could not verify if this department is used in active surveys. Please try again.', 'error')
      return
    }

    setDeleteTarget({ type: 'department', id: dept.id, name: dept.name })
    setShowDeleteModal(true)
  }

  const deleteDepartment = async (deptId: string) => {
    try {
      const { error } = await supabaseClient.from('departments').delete().eq('id', deptId)

      if (error) throw error

      setShowDeleteModal(false)
      setDeleteTarget(null)
      fetchDepartmentsWithPrograms()
    } catch (err: any) {
      console.error('Error deleting department:', err)
      showMessage('Delete Failed', err.message || 'Failed to delete department. Please try again.', 'error')
    }
  }

  // Program CRUD operations
  const openAddProgramModal = (deptId: string) => {
    setSelectedDepartmentId(deptId)
    setEditingProgram(null)
    setProgramForm({ name: '', code: '', full_name: '', description: '', display_order: 0 })
    setShowProgramModal(true)
  }

  const openEditProgramModal = (program: Program) => {
    setEditingProgram(program)
    setSelectedDepartmentId(program.department_id)
    setProgramForm({
      name: program.name,
      code: program.code,
      full_name: program.full_name || '',
      description: program.description || '',
      display_order: program.display_order || 0,
    })
    setShowProgramModal(true)
  }

  const saveProgram = async () => {
    try {
      if (!programForm.name || !programForm.code || !selectedDepartmentId) {
        showMessage('Missing Required Fields', 'Please fill in Program Name and Code.', 'warning')
        return
      }

      if (editingProgram) {
        // Update existing program
        const { error } = await supabaseClient
          .from('programs')
          .update({
            name: programForm.name,
            code: programForm.code,
            full_name: programForm.full_name || null,
            description: programForm.description || null,
            display_order: programForm.display_order || null,
          })
          .eq('id', editingProgram.id)

        if (error) throw error
      } else {
        // Insert new program
        const { error } = await supabaseClient.from('programs').insert({
          department_id: selectedDepartmentId,
          name: programForm.name,
          code: programForm.code,
          full_name: programForm.full_name || null,
          description: programForm.description || null,
          display_order: programForm.display_order || null,
        })

        if (error) throw error
      }

      setShowProgramModal(false)
      fetchDepartmentsWithPrograms()
    } catch (err: any) {
      console.error('Error saving program:', err)
      showMessage('Save Failed', err.message || 'Failed to save program. Please try again.', 'error')
    }
  }

  const openDeleteProgramModal = async (program: Program) => {
    try {
      const programWithPEOs = departments
        .flatMap(d => d.programs || [])
        .find(p => p.id === program.id) as ProgramWithPEOs | undefined
      const peoIds = programWithPEOs?.peos?.map(peo => peo.id) || []

      const { data: surveys, error: surveysError } = await supabaseClient
        .from('surveys')
        .select('id, title, target_programs, questions')
        .neq('status', 'draft')

      if (surveysError) throw surveysError

      const allSurveys = surveys || []
      const byProgram = allSurveys.filter((s: any) => s.target_programs?.includes(program.code))
      const byPEO = allSurveys.filter((s: any) =>
        peoIds.length > 0 && Array.isArray(s.questions) &&
        s.questions.some((q: any) => q.peo_ids?.some((id: string) => peoIds.includes(id)))
      )

      const seen = new Set<string>()
      const blocking = [...byProgram, ...byPEO].filter(s => {
        if (seen.has(s.id)) return false
        seen.add(s.id)
        return true
      })

      if (blocking.length > 0) {
        const names = blocking.map((s: any) => `"${s.title}"`).join(', ')
        const reasons: string[] = []
        if (byProgram.length > 0) reasons.push('deployed to this program')
        if (byPEO.length > 0) reasons.push('linked to PEOs/POs from this program')
        const reasonText = reasons.join(' or ')
        showMessage(
          'Cannot Delete Program',
          `This program cannot be deleted because active survey(s) ${names} ${blocking.length === 1 ? 'is' : 'are'} currently ${reasonText}. Please delete or update those surveys first.`,
          'warning'
        )
        return
      }
    } catch {
      showMessage('Check Failed', 'Could not verify if this program is used in active surveys. Please try again.', 'error')
      return
    }

    setDeleteTarget({ type: 'program', id: program.id, name: program.name })
    setShowDeleteModal(true)
  }

  const deleteProgram = async (programId: string) => {
    try {
      const { error } = await supabaseClient.from('programs').delete().eq('id', programId)

      if (error) throw error

      setShowDeleteModal(false)
      setDeleteTarget(null)
      fetchDepartmentsWithPrograms()
    } catch (err: any) {
      console.error('Error deleting program:', err)
      showMessage('Delete Failed', err.message || 'Failed to delete program. Please try again.', 'error')
    }
  }

  // PEO/PO CRUD operations
  const openAddPEOModal = (programId: string, outcomeType: OutcomeType = 'PEO') => {
    setSelectedProgramId(programId)
    setEditingPEO(null)
    
    // Calculate next available number based on existing outcomes
    let nextNumber = 1
    const program = departments
      .flatMap(dept => dept.programs || [])
      .find(prog => prog.id === programId) as ProgramWithPEOs | undefined
    
    if (program?.peos) {
      const existingNumbers = program.peos
        .filter((peo: ProgramEducationalOutcome) => peo.outcome_type === outcomeType)
        .map((peo: ProgramEducationalOutcome) => peo.peo_number)
      
      if (existingNumbers.length > 0) {
        nextNumber = Math.max(...existingNumbers) + 1
      }
    }
    
    setPEOForm({ peo_number: nextNumber, title: '', description: '', keywords: [], outcome_type: outcomeType })
    setSelectedCategory('') // Reset category selection
    setKeywordInput('') // Reset keyword input
    setShowKeywordSuggestions(false) // Hide suggestions
    setShowPEOModal(true)
  }

  const openEditPEOModal = (peo: ProgramEducationalOutcome) => {
    setEditingPEO(peo)
    setSelectedProgramId(peo.program_id)
    setPEOForm({
      peo_number: peo.peo_number,
      title: peo.title,
      description: peo.description || '',
      keywords: peo.keywords || [],
      outcome_type: peo.outcome_type || 'PEO',
    })
    setSelectedCategory('') // Reset category selection
    setKeywordInput('') // Reset keyword input
    setShowKeywordSuggestions(false) // Hide suggestions
    setShowPEOModal(true)
  }

  const savePEO = async () => {
    try {
      // Validation
      if (!peoForm.title || !selectedProgramId) {
        showMessage('Missing Required Fields', 'Please fill in the Title field.', 'warning')
        return
      }

      if (!peoForm.keywords || peoForm.keywords.length === 0) {
        showMessage('Keywords Required', 'Please select at least one keyword for job matching.', 'warning')
        return
      }

      // Check for duplicate PEO/PO number when adding new
      if (!editingPEO) {
        const { data: existingOutcomes, error: checkError } = await supabaseClient
          .from('program_educational_outcomes')
          .select('id, peo_number, outcome_type')
          .eq('program_id', selectedProgramId)
          .eq('outcome_type', peoForm.outcome_type)
          .eq('peo_number', peoForm.peo_number)

        if (checkError) throw checkError

        if (existingOutcomes && existingOutcomes.length > 0) {
          showMessage('Duplicate Number', `${peoForm.outcome_type} ${peoForm.peo_number} already exists for this program. Please use a different number.`, 'warning')
          return
        }
      }

      if (editingPEO) {
        // Update existing PEO/PO
        const { error } = await supabaseClient
          .from('program_educational_outcomes')
          .update({
            peo_number: peoForm.peo_number,
            title: peoForm.title,
            description: peoForm.description || null,
            keywords: peoForm.keywords,
            outcome_type: peoForm.outcome_type,
          })
          .eq('id', editingPEO.id)

        if (error) throw error
      } else {
        // Insert new PEO/PO
        const { error } = await supabaseClient.from('program_educational_outcomes').insert({
          program_id: selectedProgramId,
          peo_number: peoForm.peo_number,
          title: peoForm.title,
          description: peoForm.description || null,
          keywords: peoForm.keywords,
          outcome_type: peoForm.outcome_type,
        })

        if (error) throw error
      }

      setShowPEOModal(false)
      setSelectedCategory('') // Reset category selection
      setKeywordInput('') // Reset keyword input
      setShowKeywordSuggestions(false) // Hide suggestions
      fetchDepartmentsWithPrograms()
    } catch (err: any) {
      console.error('Error saving outcome:', err)
      
      // Provide user-friendly error messages
      if (err.code === '23505') {
        // PostgreSQL unique constraint violation
        showMessage('Duplicate Number', `${peoForm.outcome_type} ${peoForm.peo_number} already exists for this program. Please use a different number.`, 'warning')
      } else {
        showMessage('Save Failed', err.message || 'Failed to save outcome. Please try again.', 'error')
      }
    }
  }

  const openDeletePEOModal = async (peo: ProgramEducationalOutcome) => {
    try {
      const { data: surveys, error: surveysError } = await supabaseClient
        .from('surveys')
        .select('id, title, questions')
        .neq('status', 'draft')

      if (surveysError) throw surveysError

      const blocking = (surveys || []).filter((s: any) =>
        Array.isArray(s.questions) &&
        s.questions.some((q: any) => q.peo_ids?.includes(peo.id))
      )

      if (blocking.length > 0) {
        const names = blocking.map((s: any) => `"${s.title}"`).join(', ')
        showMessage(
          'Cannot Delete PEO/PO',
          `This PEO/PO cannot be deleted because active survey(s) ${names} ${blocking.length === 1 ? 'has' : 'have'} questions linked to it. Please delete or update those surveys first.`,
          'warning'
        )
        return
      }
    } catch {
      showMessage('Check Failed', 'Could not verify if this PEO/PO is used in active surveys. Please try again.', 'error')
      return
    }

    setDeleteTarget({ type: 'peo', id: peo.id, name: peo.title })
    setShowDeleteModal(true)
  }

  const deletePEO = async (peoId: string) => {
    try {
      const { error } = await supabaseClient.from('program_educational_outcomes').delete().eq('id', peoId)

      if (error) throw error

      setShowDeleteModal(false)
      setDeleteTarget(null)
      fetchDepartmentsWithPrograms()
    } catch (err: any) {
      console.error('Error deleting PEO:', err)
      showMessage('Delete Failed', err.message || 'Failed to delete outcome. Please try again.', 'error')
    }
  }

  // Keyword management
  const removeKeyword = (keyword: string) => {
    setPEOForm({
      ...peoForm,
      keywords: peoForm.keywords.filter((k) => k !== keyword),
    })
  }

  // Get filtered suggestions based on input
  const getKeywordSuggestions = (): string[] => {
    // If no input and no category, return empty (only show when typing or category selected)
    if (!keywordInput && !selectedCategory) return []
    
    // Get keywords from selected category or all categories
    let allAvailableKeywords: string[] = []
    
    if (selectedCategory) {
      // Filter to specific category
      allAvailableKeywords = KEYWORD_CATEGORIES[selectedCategory as keyof typeof KEYWORD_CATEGORIES] || []
    } else {
      // Get all keywords from all categories
      allAvailableKeywords = Object.values(KEYWORD_CATEGORIES).flat()
    }
    
    // Filter by input text and exclude already selected keywords
    let filtered = allAvailableKeywords.filter(k => !peoForm.keywords.includes(k))
    
    if (keywordInput) {
      filtered = filtered.filter(k => 
        k.toLowerCase().includes(keywordInput.toLowerCase())
      )
    }
    
    return filtered.slice(0, 15) // Limit to 15 suggestions
  }

  // Add keyword (from dropdown or custom)
  const addKeyword = (keyword: string) => {
    const trimmedKeyword = keyword.trim()
    if (!trimmedKeyword) return
    
    // Check if already exists
    if (peoForm.keywords.includes(trimmedKeyword)) {
      showMessage('Duplicate Keyword', 'This keyword has already been added.', 'warning')
      return
    }
    
    setPEOForm({
      ...peoForm,
      keywords: [...peoForm.keywords, trimmedKeyword],
    })
    setKeywordInput('')
    setShowKeywordSuggestions(false)
  }

  // Handle keyword input submission
  const handleKeywordInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addKeyword(keywordInput)
    } else if (e.key === 'Escape') {
      setShowKeywordSuggestions(false)
    }
  }

  // Toggle expansion
  const toggleDepartment = (deptId: string) => {
    const newExpanded = new Set(expandedDepartments)
    if (newExpanded.has(deptId)) {
      newExpanded.delete(deptId)
    } else {
      newExpanded.add(deptId)
    }
    setExpandedDepartments(newExpanded)
  }

  const toggleProgram = (programId: string) => {
    const newExpanded = new Set(expandedPrograms)
    if (newExpanded.has(programId)) {
      newExpanded.delete(programId)
    } else {
      newExpanded.add(programId)
    }
    setExpandedPrograms(newExpanded)
    setOpenAddMenu(null) // Close add menu when toggling
  }

  if (loading || scopeLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="bg-blue-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden mb-8">
        <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
        <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
              <GraduationCap className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Manage Programs, PEOs and POs</h1>
              <p className="text-blue-200 mt-1">
                Manage academic departments, programs, Program Educational Outcomes (PEOs), and Program Outcomes (POs)
              </p>
              <p className="text-sm text-blue-300 mt-2">
                <span className="font-medium text-white">PEOs</span> describe long-term career accomplishments (3-5 years post-graduation).
                <span className="font-medium text-white ml-3">POs</span> define immediate, measurable skills graduates possess upon graduation.
              </p>
            </div>
          </div>
          {isSuperAdmin && (
            <button
              onClick={openAddDepartmentModal}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-400 text-gray-900 rounded-lg hover:bg-yellow-300 transition shrink-0 font-semibold"
            >
              <Plus className="h-5 w-5" />
              Add Department
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Search departments or programs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        />
      </div>

      {/* Departments List */}
      <div className="space-y-4">
        {filteredDepartments.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {searchQuery.trim() ? `No results for "${searchQuery}"` : 'No departments found. Add a department to get started.'}
          </div>
        ) : (
          filteredDepartments.map((dept) => (
            <div key={dept.id} className="bg-white rounded-lg shadow border border-gray-200">
              {/* Department Header */}
              <div className="p-4 flex items-center justify-between hover:bg-gray-50 transition">
                <button
                  onClick={() => toggleDepartment(dept.id)}
                  className="flex items-center gap-3 flex-1 text-left"
                >
                  {expandedDepartments.has(dept.id) ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {dept.abbreviation} - {dept.name}
                      </h3>
                      {!canManageDepartment(dept.name) && (
                        <span className="text-xs text-gray-500 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded">
                          Read-only
                        </span>
                      )}
                    </div>
                    {dept.description && (
                      <p className="text-sm text-gray-600 mt-1">{dept.description}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {dept.programs?.length || 0} program(s)
                    </p>
                  </div>
                </button>
                <div className="flex items-center gap-2">
                  {canManageDepartment(dept.name) && (
                    <button
                      onClick={() => openAddProgramModal(dept.id)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded transition"
                      title="Add Program"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  )}
                  {isSuperAdmin && (
                    <>
                      <button
                        onClick={() => openEditDepartmentModal(dept)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded transition"
                        title="Edit Department"
                      >
                        <Edit2 className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openDeleteDepartmentModal(dept)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                        title="Delete Department"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Programs List */}
              {expandedDepartments.has(dept.id) && (
                <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
                  {!dept.programs || dept.programs.length === 0 ? (
                    <p className="text-sm text-gray-500 py-2">
                      No programs in this department. Click + to add one.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {(dept.programs as ProgramWithPEOs[]).map((program) => (
                        <div key={program.id} className="bg-white rounded border border-gray-200">
                          {/* Program Header */}
                          <div className="p-3 flex items-center justify-between hover:bg-gray-50 transition">
                            <button
                              onClick={() => toggleProgram(program.id)}
                              className="flex items-center gap-3 flex-1 text-left"
                            >
                              {expandedPrograms.has(program.id) ? (
                                <ChevronDown className="h-4 w-4 text-gray-400 ml-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-gray-400 ml-4" />
                              )}
                              <div>
                                <h4 className="font-medium text-gray-900">
                                  {program.code} - {program.name}
                                </h4>
                                {program.description && (
                                  <p className="text-xs text-gray-600 mt-1">{program.description}</p>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                  {program.peos?.filter(p => p.outcome_type === 'PEO').length || 0} PEO(s), {program.peos?.filter(p => p.outcome_type === 'PO').length || 0} PO(s)
                                </p>
                              </div>
                            </button>
                            <div className="flex items-center gap-2">
                              {canManageDepartment(dept.name) && (
                                <>
                                  {/* Add PEO/PO Dropdown Button */}
                                  <div className="relative">
                                    <button
                                      onClick={() => setOpenAddMenu(openAddMenu === program.id ? null : program.id)}
                                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition text-sm"
                                      title="Add PEO or PO"
                                    >
                                      <Plus className="h-4 w-4" />
                                    </button>
                                    {openAddMenu === program.id && (
                                      <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[140px]">
                                        <button
                                          onClick={() => {
                                            openAddPEOModal(program.id, 'PEO')
                                            setOpenAddMenu(null)
                                          }}
                                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                                        >
                                          Add PEO
                                        </button>
                                        <button
                                          onClick={() => {
                                            openAddPEOModal(program.id, 'PO')
                                            setOpenAddMenu(null)
                                          }}
                                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                                        >
                                          Add PO
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => openEditProgramModal(program)}
                                    className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition"
                                    title="Edit Program"
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => openDeleteProgramModal(program)}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded transition"
                                    title="Delete Program"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>

                          {/* PEO/PO Tabs and List */}
                          {expandedPrograms.has(program.id) && (
                            <div className="border-t border-gray-200 bg-gray-50 px-3 py-2">
                              {/* Tabs for PEO vs PO */}
                              <div className="flex gap-2 mb-3 ml-8">
                                <button
                                  onClick={() => setActiveOutcomeTab('PEO')}
                                  className={`px-3 py-1.5 rounded text-sm font-medium transition ${
                                    activeOutcomeTab === 'PEO'
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                                  }`}
                                >
                                  PEOs ({program.peos?.filter(p => p.outcome_type === 'PEO').length || 0})
                                </button>
                                <button
                                  onClick={() => setActiveOutcomeTab('PO')}
                                  className={`px-3 py-1.5 rounded text-sm font-medium transition ${
                                    activeOutcomeTab === 'PO'
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                                  }`}
                                >
                                  POs ({program.peos?.filter(p => p.outcome_type === 'PO').length || 0})
                                </button>
                              </div>
                              
                              {/* Filtered Outcomes List */}
                              {(() => {
                                const filteredOutcomes = program.peos?.filter(p => p.outcome_type === activeOutcomeTab) || []
                                return filteredOutcomes.length === 0 ? (
                                  <p className="text-xs text-gray-500 py-2 ml-8">
                                    No {activeOutcomeTab}s defined. Click + to add one.
                                  </p>
                                ) : (
                                  <div className="space-y-2 ml-8">
                                    {filteredOutcomes.map((peo) => (
                                    <div
                                      key={peo.id}
                                      className="bg-white rounded border border-gray-200 p-3"
                                    >
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                              {peo.outcome_type} {peo.peo_number}
                                            </span>
                                            <h5 className="text-sm font-medium text-gray-900">
                                              {peo.title}
                                            </h5>
                                          </div>
                                          {peo.description && (
                                            <p className="text-xs text-gray-600 mt-2">
                                              {peo.description}
                                            </p>
                                          )}
                                          {peo.keywords && peo.keywords.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-2">
                                              {peo.keywords.map((keyword: string, idx: number) => (
                                                <span
                                                  key={idx}
                                                  className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded"
                                                >
                                                  {keyword}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-1 ml-2">
                                          {canManageDepartment(dept.name) && (
                                            <>
                                              <button
                                                onClick={() => openEditPEOModal(peo)}
                                                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition"
                                                title={`Edit ${peo.outcome_type}`}
                                              >
                                                <Edit2 className="h-3.5 w-3.5" />
                                              </button>
                                              <button
                                                onClick={() => openDeletePEOModal(peo)}
                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded transition"
                                                title={`Delete ${peo.outcome_type}`}
                                              >
                                                <Trash2 className="h-3.5 w-3.5" />
                                              </button>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                )
                              })()}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Department Modal */}
      {showDepartmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {editingDepartment ? 'Edit Department' : 'Add Department'}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={departmentForm.name}
                    onChange={(e) => setDepartmentForm({ ...departmentForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., College of Computer Studies"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Abbreviation <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={departmentForm.abbreviation}
                    onChange={(e) =>
                      setDepartmentForm({ ...departmentForm, abbreviation: e.target.value.replace(/\s/g, '').toUpperCase() })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., CCS"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={departmentForm.description}
                    onChange={(e) =>
                      setDepartmentForm({ ...departmentForm, description: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder="Optional description"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={departmentForm.display_order}
                    onChange={(e) =>
                      setDepartmentForm({ ...departmentForm, display_order: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                  />
                </div>
                {!editingDepartment && (
                  <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="createAdminCode"
                      checked={departmentForm.createAdminCode}
                      onChange={(e) =>
                        setDepartmentForm({ ...departmentForm, createAdminCode: e.target.checked })
                      }
                      className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="createAdminCode" className="text-sm text-gray-700 flex-1">
                      <span className="font-medium">Create department admin code</span>
                      <p className="text-xs text-gray-600 mt-1">
                        Generate a 24-hour admin code for this department (e.g., {departmentForm.abbreviation ? `${departmentForm.abbreviation.toUpperCase()}_ADMIN_2026` : 'CCS_ADMIN_2026'})
                      </p>
                    </label>
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={saveDepartment}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  <Save className="h-4 w-4" />
                  Save
                </button>
                <button
                  onClick={() => setShowDepartmentModal(false)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Program Modal */}
      {showProgramModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {editingProgram ? 'Edit Program' : 'Add Program'}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={programForm.name}
                    onChange={(e) => setProgramForm({ ...programForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Computer Science"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={programForm.code}
                    onChange={(e) => setProgramForm({ ...programForm, code: e.target.value.replace(/\s/g, '').toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., BSCS"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={programForm.full_name}
                    onChange={(e) => setProgramForm({ ...programForm, full_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., BSCS – Computer Science"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={programForm.description}
                    onChange={(e) => setProgramForm({ ...programForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder="Optional description"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={programForm.display_order}
                    onChange={(e) =>
                      setProgramForm({ ...programForm, display_order: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={saveProgram}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  <Save className="h-4 w-4" />
                  Save
                </button>
                <button
                  onClick={() => setShowProgramModal(false)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PEO/PO Modal */}
      {showPEOModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {editingPEO ? `Edit ${peoForm.outcome_type}` : `Add ${peoForm.outcome_type}`}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {peoForm.outcome_type} Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={peoForm.peo_number}
                    onChange={(e) =>
                      setPEOForm({ ...peoForm, peo_number: parseInt(e.target.value) || 1 })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={peoForm.title}
                    onChange={(e) => setPEOForm({ ...peoForm, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Software Development Excellence"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={peoForm.description}
                    onChange={(e) => setPEOForm({ ...peoForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder="Describe the educational outcome"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Keywords (for job matching algorithm) <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Type or browse keywords. Select at least one relevant skill or competency that this {peoForm.outcome_type} helps develop.
                  </p>
                  
                  {/* Category Selection Dropdown (Optional Filter) */}
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Category Filter (optional)
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
                  >
                    <option value="">All Categories</option>
                    {Object.keys(KEYWORD_CATEGORIES).map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  
                  {/* Custom Keyword Input with Suggestions */}
                  <div className="relative mb-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={keywordInput}
                        onChange={(e) => {
                          setKeywordInput(e.target.value)
                          setShowKeywordSuggestions(true)
                        }}
                        onKeyDown={handleKeywordInputKeyDown}
                        onFocus={() => setShowKeywordSuggestions(true)}
                        onBlur={() => {
                          if (!isHoveringSuggestions) setShowKeywordSuggestions(false)
                        }}
                        placeholder={selectedCategory ? `Type keyword from ${selectedCategory}...` : "Type any keyword or browse all..."}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => addKeyword(keywordInput)}
                        disabled={!keywordInput.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>
                    
                    {/* Suggestions Dropdown */}
                    {showKeywordSuggestions && getKeywordSuggestions().length > 0 && (
                      <div
                        className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                        onMouseEnter={() => setIsHoveringSuggestions(true)}
                        onMouseLeave={() => setIsHoveringSuggestions(false)}
                      >
                        {getKeywordSuggestions().map((suggestion) => (
                          <button
                            key={suggestion}
                            type="button"
                            onClick={() => {
                              addKeyword(suggestion)
                              setIsHoveringSuggestions(false)
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 transition text-sm"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    <p className="text-xs text-gray-500 mt-1">
                      Select from the dropdown or type your own keyword and click Add.
                    </p>
                  </div>
                  
                  {/* Selected Keywords Display */}
                  {peoForm.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {peoForm.keywords.map((keyword) => {
                        return (
                          <span
                            key={keyword}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm bg-blue-50 text-blue-700 border border-blue-200"
                          >
                            {keyword}
                            <button
                              type="button"
                              onClick={() => removeKeyword(keyword)}
                              className="hover:opacity-70"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        )
                      })}
                    </div>
                  )}
                  {peoForm.keywords.length === 0 && (
                    <p className="text-xs text-red-500 italic mt-2">
                      ⚠️ At least one keyword is required. Select keywords from the dropdown above.
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={savePEO}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  <Save className="h-4 w-4" />
                  Save
                </button>
                <button
                  onClick={() => {
                    setShowPEOModal(false)
                    setSelectedCategory('') // Reset category selection
                    setKeywordInput('') // Reset keyword input
                    setShowKeywordSuggestions(false) // Hide suggestions
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Confirm Delete</h2>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700 mb-2">
                  Are you sure you want to delete{' '}
                  <span className="font-semibold">{deleteTarget.name}</span>?
                </p>
                {deleteTarget.type === 'department' && (
                  <p className="text-sm text-red-600 mt-2">
                    ⚠️ This will also delete all associated programs and PEOs.
                  </p>
                )}
                {deleteTarget.type === 'program' && (
                  <p className="text-sm text-red-600 mt-2">
                    ⚠️ This will also delete all associated PEOs.
                  </p>
                )}
                <p className="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (deleteTarget.type === 'department') {
                      deleteDepartment(deleteTarget.id)
                    } else if (deleteTarget.type === 'program') {
                      deleteProgram(deleteTarget.id)
                    } else if (deleteTarget.type === 'peo') {
                      deletePEO(deleteTarget.id)
                    }
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false)
                    setDeleteTarget(null)
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal (for errors, warnings, success) */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-3 rounded-full ${
                  messageModal.type === 'error' ? 'bg-red-100' :
                  messageModal.type === 'warning' ? 'bg-yellow-100' :
                  'bg-green-100'
                }`}>
                  <AlertCircle className={`h-6 w-6 ${
                    messageModal.type === 'error' ? 'text-red-600' :
                    messageModal.type === 'warning' ? 'text-yellow-600' :
                    'text-green-600'
                  }`} />
                </div>
                <h2 className="text-xl font-bold text-gray-900">{messageModal.title}</h2>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700">{messageModal.message}</p>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setShowMessageModal(false)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
